import React, { useEffect, createElement } from 'react';
import { useLanguage } from '../context/LanguageContext';
function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}
function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}
function setJsonLd(id: string, data: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
const seo = {
  es: {
    title: 'Tairu Aesthetics | Tratamientos Faciales Personalizados en Elmwood Park, NJ',
    description: 'Tairu Aesthetics ofrece tratamientos faciales personalizados para acné, rejuvenecimiento, Hydrafacial y más. Licenciada cosmetóloga con 5 estrellas en Google. Elmwood Park, NJ. Abierto 7 días.',
    keywords: 'tratamiento facial, acné, hydrafacial, dermaplaning, rejuvenecimiento facial, esteticista, cosmetóloga, Elmwood Park NJ, cuidado de la piel, peeling químico, microdermoabrasión, exosomas, lifting de pestañas'
  },
  en: {
    title: 'Tairu Aesthetics | Personalized Facial Treatments in Elmwood Park, NJ',
    description: 'Tairu Aesthetics offers personalized facial treatments for acne, rejuvenation, Hydrafacial and more. Licensed cosmetologist with 5 stars on Google. Elmwood Park, NJ. Open 7 days.',
    keywords: 'facial treatment, acne, hydrafacial, dermaplaning, facial rejuvenation, esthetician, cosmetologist, Elmwood Park NJ, skincare, chemical peel, microdermabrasion, exosomes, lash lift'
  }
};
const SITE_URL = 'https://tairuaesthetics.com';
export function SEOHead() {
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const t = seo[language];
    // Title
    document.title = t.title;
    // Basic meta
    setMeta('description', t.description);
    setMeta('keywords', t.keywords);
    setMeta('robots', 'index, follow');
    setMeta('author', 'Tairu Aesthetics');
    setMeta('geo.region', 'US-NJ');
    setMeta('geo.placename', 'Elmwood Park');
    // Open Graph
    setMeta('og:type', 'website', true);
    setMeta('og:title', t.title, true);
    setMeta('og:description', t.description, true);
    setMeta('og:url', SITE_URL, true);
    setMeta('og:site_name', 'Tairu Aesthetics', true);
    setMeta('og:locale', language === 'es' ? 'es_ES' : 'en_US', true);
    setMeta('og:image', 'https://cdn.magicpatterns.com/uploads/oa5tF97CyYwzP5bMPijEJz/banner_new.png', true);
    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', t.title);
    setMeta('twitter:description', t.description);
    setMeta('twitter:image', 'https://cdn.magicpatterns.com/uploads/oa5tF97CyYwzP5bMPijEJz/banner_new.png');
    // Canonical
    setLink('canonical', SITE_URL);
    // Schema: LocalBusiness
    setJsonLd('schema-local-business', {
      '@context': 'https://schema.org',
      '@type': 'BeautySalon',
      name: 'Tairu Aesthetics',
      image: 'https://cdn.magicpatterns.com/uploads/oa5tF97CyYwzP5bMPijEJz/banner_new.png',
      url: SITE_URL,
      telephone: '+1-551-332-8938',
      priceRange: '$$',
      description: t.description,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '100 Broadway, Suite #114, Phenix Salon Suites',
        addressLocality: 'Elmwood Park',
        addressRegion: 'NJ',
        postalCode: '07407',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.919119,
        longitude: -74.123202
      },
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '19:00'
      }, {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '20:00'
      }, {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Sunday',
        opens: '10:00',
        closes: '16:30'
      }],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5.0',
        bestRating: '5',
        worstRating: '1',
        ratingCount: '30'
      },
      sameAs: ['https://www.instagram.com/tairu.aesthetics/', 'https://www.facebook.com/katherine.ospino.95063'],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: language === 'es' ? 'Tratamientos Faciales' : 'Facial Treatments',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'es' ? 'Tratamiento para el Acné' : 'Acne Treatment',
            description: language === 'es' ? 'Procedimiento estético 100% personalizado para controlar el acné activo.' : 'A 100% personalized aesthetic procedure to control active acne.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Hydrafacial'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'es' ? 'Microdermoabrasión' : 'Microdermabrasion'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Dermaplaning'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: language === 'es' ? 'Exosomas' : 'Exosomes'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'GLOW2 Facial'
          }
        }]
      }
    });
    // Schema: FAQPage
    setJsonLd('schema-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: language === 'es' ? 'No sé si funciona para mi tipo de piel' : "I don't know if it works for my skin type",
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'es' ? 'En Tairu evaluamos tu piel antes de cualquier tratamiento. No aplicamos nada sin un diagnóstico previo.' : 'At Tairu we evaluate your skin before any treatment. We do not apply anything without a prior diagnosis.'
        }
      }, {
        '@type': 'Question',
        name: language === 'es' ? '¿Y si tengo piel sensible?' : 'What if I have sensitive skin?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'es' ? 'Trabajamos con protocolos adaptados incluso para pieles hipersensibles.' : 'We work with protocols adapted even for hypersensitive skin.'
        }
      }, {
        '@type': 'Question',
        name: language === 'es' ? 'Ya probé muchas cosas y nada funcionó' : "I've tried many things and nothing worked",
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'es' ? 'Lo que no funcionó antes fue diseñado para otra persona. En Tairu diseñamos un protocolo exclusivamente para ti.' : "What didn't work before was designed for someone else. At Tairu we design a protocol exclusively for you."
        }
      }, {
        '@type': 'Question',
        name: language === 'es' ? '¿Es costoso?' : 'Is it expensive?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'es' ? 'Una sesión correctiva bien aplicada es la inversión más inteligente que puedes hacer por tu piel.' : 'A well-applied corrective session is the smartest investment you can make for your skin.'
        }
      }]
    });
  }, [language]);
  return null;
}